// RTK Query slice for inbound emails, knowledge base, and inbox settings
import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "./baseQuery";

export const inboundEmailsApi = createApi({
  reducerPath: "inboundEmailsApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  keepUnusedDataFor: 120,
  tagTypes: ["InboundEmails", "KnowledgeBase", "InboxSettings", "UnreadCount"],
  endpoints: (builder) => ({
    // ── Inbox ──
    getInboundEmails: builder.query({
      query: ({ page = 1, limit = 50, status, search } = {}) => {
        const params = new URLSearchParams();
        params.set("page", page);
        params.set("limit", limit);
        if (status) params.set("status", status);
        if (search) params.set("search", search);
        return {
          url: `api/inbound-emails?${params.toString()}`,
          method: "GET",
          headers: { "Cache-Control": "no-cache" },
        };
      },
      providesTags: ["InboundEmails"],
    }),

    getInboundEmailById: builder.query({
      query: (id) => ({
        url: `api/inbound-emails/${id}`,
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      providesTags: (result, error, id) => [{ type: "InboundEmails", id }],
    }),

    getUnreadCount: builder.query({
      query: () => ({
        url: "api/inbound-emails/unread-count",
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      providesTags: ["UnreadCount"],
    }),

    updateEmailStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `api/inbound-emails/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["InboundEmails", "UnreadCount"],
    }),

    replyToEmail: builder.mutation({
      query: ({ id, body, lang }) => ({
        url: `api/inbound-emails/${id}/reply`,
        method: "POST",
        body: { body, lang },
      }),
      invalidatesTags: (result, error, { id }) => [
        "InboundEmails",
        { type: "InboundEmails", id },
      ],
    }),

    deleteEmail: builder.mutation({
      query: (id) => ({
        url: `api/inbound-emails/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["InboundEmails", "UnreadCount"],
    }),

    // ── Knowledge Base ──
    getKnowledgeBase: builder.query({
      query: () => ({
        url: "api/email-knowledge-base",
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      providesTags: ["KnowledgeBase"],
    }),

    createKBEntry: builder.mutation({
      query: (body) => ({
        url: "api/email-knowledge-base",
        method: "POST",
        body,
      }),
      invalidatesTags: ["KnowledgeBase"],
    }),

    updateKBEntry: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/email-knowledge-base/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["KnowledgeBase"],
    }),

    deleteKBEntry: builder.mutation({
      query: (id) => ({
        url: `api/email-knowledge-base/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["KnowledgeBase"],
    }),

    toggleKBEntry: builder.mutation({
      query: (id) => ({
        url: `api/email-knowledge-base/${id}/toggle`,
        method: "PATCH",
      }),
      invalidatesTags: ["KnowledgeBase"],
    }),

    reorderKBEntries: builder.mutation({
      query: (orderedIds) => ({
        url: "api/email-knowledge-base/reorder",
        method: "POST",
        body: { orderedIds },
      }),
      invalidatesTags: ["KnowledgeBase"],
    }),

    getSuggestedQuestions: builder.query({
      query: () => ({
        url: "api/email-knowledge-base/suggestions",
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      providesTags: ["KnowledgeBase"],
    }),

    // ── Inbox Settings ──
    getInboxSettings: builder.query({
      query: () => ({
        url: "api/email-inbox-settings",
        method: "GET",
        headers: { "Cache-Control": "no-cache" },
      }),
      providesTags: ["InboxSettings"],
    }),

    updateInboxSettings: builder.mutation({
      query: (body) => ({
        url: "api/email-inbox-settings",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["InboxSettings"],
    }),

    generateForwardingAddress: builder.mutation({
      query: () => ({
        url: "api/email-inbox-settings/generate-address",
        method: "POST",
      }),
      invalidatesTags: ["InboxSettings"],
    }),

    // ── Auto-Reply Simulation ──
    simulateAutoReply: builder.mutation({
      query: (emailId) => ({
        url: "api/email-auto-reply/simulate",
        method: "POST",
        body: { emailId },
      }),
      invalidatesTags: (result, error, emailId) => [
        { type: "InboundEmails", id: emailId },
      ],
    }),

    previewAutoReply: builder.mutation({
      query: (text) => ({
        url: "api/email-auto-reply/preview",
        method: "POST",
        body: { text },
      }),
    }),
  }),
});

export const {
  useGetInboundEmailsQuery,
  useGetInboundEmailByIdQuery,
  useGetUnreadCountQuery,
  useUpdateEmailStatusMutation,
  useReplyToEmailMutation,
  useDeleteEmailMutation,
  useGetKnowledgeBaseQuery,
  useCreateKBEntryMutation,
  useUpdateKBEntryMutation,
  useDeleteKBEntryMutation,
  useToggleKBEntryMutation,
  useReorderKBEntriesMutation,
  useGetSuggestedQuestionsQuery,
  useGetInboxSettingsQuery,
  useUpdateInboxSettingsMutation,
  useGenerateForwardingAddressMutation,
  useSimulateAutoReplyMutation,
  usePreviewAutoReplyMutation,
} = inboundEmailsApi;
